import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
} from "react";
import { GroupedBarChart } from "./charts/GroupedBarChart";
import {
  Box,
  createColumnHelper,
  DataTable,
  DataTableCellContent,
  DataTableColumnSettings,
  Dialog,
  DialogPopup,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  ScrollArea,
  Separator,
  Spinner,
  useElementSize,
  Text,
  type DataTableColumnDef,
  type DataTableGroupType,
  type SortingState,
} from "@salestrak/ui";
import { IssueViewContext } from "#app/components/issue-view";
import type {
  DashboardDrilldownActivity,
  DashboardDrilldownIssue,
  DashboardLabels,
} from "#app/lib/data";
import { formatDateShort } from "../lib/utils";
import { CHART_PALETTE_WITH_GRADIENT } from "../lib/constants";
import { useDashboardFilters } from "../lib/FilterContext";
import { useTableViewSettings } from "#app/lib/hooks/use-table-view-settings";
import { useDrilldownQueries } from "../hooks/use-drilldown-queries";
import {
  AgeReasonDrilldown,
  type AgeReasonGroupSummary,
} from "./AgeReasonDrilldown";
import { GoldenHourDrilldown } from "./GoldenHourDrilldown";
import {
  DrilldownIssueLabel,
  DrilldownMemberTag,
} from "./DrilldownGroupTitle";
import { DailyActivityTable } from "./DailyActivityTable";
import { StageBreakdownTable } from "./StageBreakdownTable";
import { TargetMetricDrilldown } from "./TargetMetricDrilldown";
import type { Omittable } from "@salestrak/ui";
import { at } from "#app/lib/utils/at";

// ── Modal types ──────────────────────────────────────────────────────────────

export type DrilldownType =
  | { kind: "stageChanges" }
  | { kind: "targetMetric" }
  | { kind: "ageReasons" }
  | { kind: "goldenHour"; hour: number; label: string }
  // A single-day bar carries no `endDate`; a weekly bar does. Nothing reads an
  // absent key differently from an explicit `undefined`, so the member relaxes
  // rather than making every construction site drop the key.
  | Omittable<{
      kind: "dailyActivity";
      date: string;
      endDate?: string;
      label: string;
    }>
  | { kind: "stageBreakdown"; stage: string }
  | { kind: "repActivity"; userId: string; name: string }
  | { kind: "workflowIssues"; workflow: string };

interface DrilldownDialogProps {
  drilldown: DrilldownType | null;
  onClose: () => void;
  startDate: string;
  endDate: string;
}

// ── Row type for all drilldown tables ────────────────────────────────────────

type DrilldownRow = Omittable<{
  id: string;
  _issueId?: string;
  _workflowId?: string;
  [key: string]: string | undefined;
}>;

const h = createColumnHelper<DrilldownRow>();

// Force the DataTable's internal flex-1 containers to resolve to a concrete
// height when rendered inside a Dialog (whose flex chain is unbounded).
// Uses a MutationObserver to apply height as soon as children mount.
function DataTableInDialog({
  height,
  children,
}: {
  height: number;
  children: React.ReactNode;
}) {
  const refCallback = useCallback((el: HTMLDivElement | null) => {
    if (!el) return;

    const applyHeights = () => {
      const outer = el.firstElementChild as HTMLElement | null;
      if (!outer) return false;
      outer.style.height = "100%";
      const scroll = outer.firstElementChild as HTMLElement | null;
      if (scroll) {
        scroll.style.height = "100%";
        window.dispatchEvent(new Event("resize"));
      }
      return !!scroll;
    };

    if (applyHeights()) return;

    const observer = new MutationObserver(() => {
      if (applyHeights()) observer.disconnect();
    });
    observer.observe(el, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 2000);
  }, []);

  return (
    <Box
      ref={refCallback as any}
      overflow="hidden"
      direction="column"
      height={height}
      maxWidth={0}
      minWidth="100%"
      position="relative"
    >
      {children}
    </Box>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

export function DrilldownDialog({
  drilldown,
  onClose,
  startDate,
  endDate,
}: DrilldownDialogProps) {
  // The dialog fills the viewport, so its own popup height — not the window's —
  // is what the table must fit inside. Popup and header are both measured here.
  const { ref: popupRef, size: popupSize } = useElementSize<HTMLDivElement>();
  const { ref: headerRef, size: headerSize } = useElementSize<HTMLDivElement>();
  const isOpen = drilldown !== null;

  const issueViewCtx = React.useContext(IssueViewContext);
  const { selectedTeams, selectedReps, teams, analytics } =
    useDashboardFilters();
  const labels = analytics?.labels;

  // Theme-aware chart colors
  const agePaint = at(CHART_PALETTE_WITH_GRADIENT, 0);
  const projectPaint = at(CHART_PALETTE_WITH_GRADIENT, 1);
  const ageBarColor = agePaint.color;
  const ageBarGradient = agePaint.gradient;
  const projectBarColor = projectPaint.color;
  const projectBarGradient = projectPaint.gradient;
  const chartLabelColor = "var(--muted-foreground)";
  const chartRuleColor = "var(--border)";

  const [sorting, setSorting] = useState<SortingState>([]);
  // Reset sorting when the drilldown type changes — column IDs differ per kind
  const drilldownKind = drilldown?.kind;
  useEffect(() => {
    setSorting([]);
  }, [drilldownKind]);


  // Resolve team names to IDs for drill-down queries
  const teamIds = useMemo(() => {
    if (selectedTeams.length === 0) return undefined;
    return selectedTeams
      .map((name) => teams.find((t) => t.teamName === name)?.teamId)
      .filter(Boolean) as string[];
  }, [selectedTeams, teams]);
  const memberIds = selectedReps.length > 0 ? selectedReps : undefined;

  // All drilldown queries are managed by this hook
  const {
    activityData,
    activityTotal,
    issueData,
    ageReasonGroupSummaries,
    issueGroupSummaries,
    activityGroupSummaries,
    drilldownLoading,
    fetchPageActivities,
    isPaginatedActivity,
    isDailyActivity,
    isStageBreakdown,
    isWorkflowIssues,
    isAgeReasons,
    isGoldenHour,
    isTargetMetric,
    usesSpecializedView,
    sortField,
    sortDirection,
    timezone,
  } = useDrilldownQueries({
    drilldown,
    startDate,
    endDate,
    teamIds,
    memberIds,
    sorting,
  });

  // ── Daily activity chart state ────────────────────────────────────────────
  const [selectedMember, setSelectedMember] = useState<{
    id: string;
    tick: number;
  } | null>(null);
  const [chartMeasuredWidth, setChartMeasuredWidth] = useState(0);
  const roRef = React.useRef<ResizeObserver | null>(null);
  const chartPanelRef = useCallback((el: HTMLDivElement | null) => {
    if (roRef.current) {
      roRef.current.disconnect();
      roRef.current = null;
    }
    if (!el) return;
    const measure = () => setChartMeasuredWidth(el.clientWidth);
    measure();
    roRef.current = new ResizeObserver(() => measure());
    roRef.current.observe(el);
  }, []);
  useEffect(
    () => () => {
      roRef.current?.disconnect();
    },
    [],
  );

  // Daily activity chart data — build from age activities + new projects
  const dayChartData = useMemo(() => {
    if (!isDailyActivity)
      return { bars: [] as any[], ageCount: 0, newProjectCount: 0 };
    const counts = new Map<
      string,
      { age: number; newProjects: number; name: string }
    >();
    let ageCount = 0;
    let newProjectCount = 0;

    // Age updates from group summaries
    for (const s of ageReasonGroupSummaries) {
      const c = counts.get(s.userId) ?? {
        age: 0,
        newProjects: 0,
        name: s.name,
      };
      c.age += s.totalUpdates;
      ageCount += s.totalUpdates;
      counts.set(s.userId, c);
    }

    // Count new projects (issues created on this day, grouped by assignee)
    for (const issue of issueData) {
      const id = issue.assigneeUserId ?? "__unknown__";
      const name = issue.assigneeName ?? "Unknown";
      const c = counts.get(id) ?? { age: 0, newProjects: 0, name };
      c.newProjects++;
      newProjectCount++;
      counts.set(id, c);
    }

    const sorted = Array.from(counts.entries())
      .map(([id, c]) => ({ id, ...c, total: c.age + c.newProjects }))
      .sort((a, b) => b.total - a.total);

    const bars: any[] = [];
    for (const member of sorted) {
      const firstName = member.name.split(" ")[0] || "?";
      bars.push({
        value: member.age,
        frontColor: ageBarColor,
        gradientColor: ageBarGradient,
        showGradient: true,
        label: firstName,
        spacing: 2,
        userId: member.id,
      });
      bars.push({
        value: member.newProjects,
        frontColor: projectBarColor,
        gradientColor: projectBarGradient,
        showGradient: true,
        userId: member.id,
      });
    }
    return { bars, ageCount, newProjectCount };
  }, [
    isDailyActivity,
    ageReasonGroupSummaries,
    issueData,
    ageBarColor,
    ageBarGradient,
    projectBarColor,
    projectBarGradient,
  ]);

  // Map backend data to DrilldownRows
  const { title, description, columns, rows } = useDrilldownDataMemo({
    drilldown,
    activityData,
    issueData,
    labels,
    issueViewCtx,
    ageReasonGroupSummaries,
  });

  // Per-user column customization for the generic fallback table, keyed by
  // drilldown kind — each kind has its own column set. Specialized drilldown
  // tables own their customization internally.
  const fallbackTableView = useTableViewSettings(
    `drilldown:${drilldown?.kind ?? "none"}`,
  );

  const groups = useMemo<DataTableGroupType<DrilldownRow>[]>(() => {
    // For paginated drilldowns, backend handles sorting — skip client-side sort
    const sortedRows = isPaginatedActivity ? rows : [...rows];
    if (!isPaginatedActivity) {
      const sort = sorting[0];
      if (sort) {
        const { id: sortKey, desc } = sort;
        const dir = desc ? -1 : 1;
        sortedRows.sort((a, b) => {
          const aVal = a[sortKey] ?? "";
          const bVal = b[sortKey] ?? "";
          return aVal.localeCompare(bVal) * dir;
        });
      }
    }

    const byMember = new Map<string, DrilldownRow[]>();
    for (const row of sortedRows) {
      const key = row._member ?? "Unknown";
      const list = byMember.get(key);
      if (list) list.push(row);
      else byMember.set(key, [row]);
    }

    const countLabel = (count: number) => {
      switch (drilldown?.kind) {
        case "stageChanges":
          return `${count} stage change${count === 1 ? "" : "s"}`;
        case "repActivity":
          return `${count} age update${count === 1 ? "" : "s"}`;
        default:
          return `${count}`;
      }
    };

    return Array.from(byMember.entries()).map(([member, data]) => ({
      key: member,
      label: member,
      data,
      totalCount: isPaginatedActivity ? activityTotal : countLabel(data.length),
      fetchPage: isPaginatedActivity ? fetchPageActivities : undefined,
    }));
  }, [
    rows,
    drilldown?.kind,
    sorting,
    isPaginatedActivity,
    activityTotal,
    fetchPageActivities,
  ]);

  // Dialog: ~64px outer gap (fullscreen mode) + border (2px) + separator (1px)
  const tableHeight = popupSize.height - 3 - headerSize.height;

  const fillColumnId =
    drilldown?.kind === "goldenHour" ||
    drilldown?.kind === "ageReasons" ||
    drilldown?.kind === "targetMetric"
      ? "project"
      : drilldown?.kind === "dailyActivity" || drilldown?.kind === "repActivity"
        ? "issueName"
        : "name";

  const displayTitle = !drilldown
    ? ""
    : drilldown.kind === "dailyActivity"
      ? `Activity — ${drilldown.label}`
      : drilldown.kind === "stageBreakdown"
        ? `Stage — ${drilldown.stage}`
        : drilldown.kind === "workflowIssues"
          ? `Workflow — ${drilldown.workflow}`
          : isTargetMetric
            ? (labels?.targetMetricLabel ?? "Target Metric")
            : title;

  const chartPanelStyle = useMemo(
    () => ({
      width: "55%",
      overflow: "hidden" as const,
      flexShrink: 0,
      display: "flex" as const,
      flexDirection: "column" as const,
      paddingBottom: 16,
    }),
    [],
  );

  const splitLayoutStyle = useMemo(
    () => ({ height: Math.max(200, tableHeight) }),
    [tableHeight],
  );

  const splitHeight = Math.max(200, tableHeight);

  const handleClose = () => {
    setSelectedMember(null);
    onClose();
  };

  const subtitle = usesSpecializedView
    ? isDailyActivity
      ? `${dayChartData.ageCount} age updates, ${dayChartData.newProjectCount} new projects`
      : isStageBreakdown
        ? `${issueGroupSummaries.reduce((sum, s) => sum + s.totalIssues, 0)} projects in this stage`
        : isWorkflowIssues
          ? `${issueGroupSummaries.reduce((sum, s) => sum + s.totalIssues, 0)} projects in this workflow`
          : isAgeReasons || isGoldenHour
            ? `${ageReasonGroupSummaries.reduce((sum, s) => sum + s.totalUpdates, 0)} age updates`
            : isTargetMetric
              ? `${activityGroupSummaries.reduce((sum, s) => sum + s.totalChanges, 0)} records`
              : `${activityData.length} age updates`
    : description;

  const body = (
    <>
      <Separator />

        {isDailyActivity ? (
          <Box direction="row" height={splitHeight}>
            {/* Left: Chart — 55% */}
            <Box
              ref={chartPanelRef as any}
              overflow="hidden"
              direction="column"
              paddingBottom="md"
              width="55%"
              shrink={false}
            >
                <Box paddingTop="md" paddingX="lg" paddingBottom="sm">
                  <Text size="sm" weight="semibold">
                    Activity by Member
                  </Text>
                  <Box paddingTop="xs">
                    <Text size="xs" variant="muted">
                      {dayChartData.ageCount} age updates,{" "}
                      {dayChartData.newProjectCount} new projects
                    </Text>
                  </Box>
                </Box>

                <Box direction="row" gap="md" paddingX="lg" paddingBottom="sm">
                  <Box direction="row" gap="xs" align="center">
                    <Text size="xs" color="purple">
                      {"\u25CF"}
                    </Text>
                    <Text size="xs" variant="muted">
                      Age Updates
                    </Text>
                  </Box>
                  <Box direction="row" gap="xs" align="center">
                    <Text size="xs" color="teal">
                      {"\u25CF"}
                    </Text>
                    <Text size="xs" variant="muted">
                      New Projects
                    </Text>
                  </Box>
                </Box>

                {chartMeasuredWidth > 0 &&
                  dayChartData.bars.length > 0 &&
                  (() => {
                    const yAxisWidth = 40;
                    const sidePadding = 32;
                    const initialSpace = 10;
                    const endSpace = 20;
                    const availableChartWidth =
                      chartMeasuredWidth - sidePadding - yAxisWidth;
                    const usableWidth =
                      availableChartWidth - initialSpace - endSpace;
                    const chartHeight = Math.max(120, tableHeight - 180);

                    const memberCount = Math.ceil(dayChartData.bars.length / 2);
                    const pairGap = 2;
                    const MIN_BAR_WIDTH = 16;

                    const groupGapIdeal = Math.max(
                      8,
                      Math.floor((usableWidth / memberCount) * 0.2),
                    );
                    const barWidthIdeal = Math.floor(
                      (usableWidth / memberCount - pairGap - groupGapIdeal) / 2,
                    );

                    const needsScroll = barWidthIdeal < MIN_BAR_WIDTH;
                    const barWidth = needsScroll
                      ? MIN_BAR_WIDTH
                      : barWidthIdeal;
                    const groupGap = needsScroll ? 12 : groupGapIdeal;
                    const pairWidth = barWidth * 2 + pairGap;

                    const centeredBars: any[] = [];
                    for (let i = 0; i < dayChartData.bars.length; i += 2) {
                      const ageBar = dayChartData.bars[i];
                      const stageBar = dayChartData.bars[i + 1];
                      centeredBars.push({
                        ...ageBar,
                        spacing: pairGap,
                        labelWidth: pairWidth,
                        labelTextStyle: {
                          color: chartLabelColor,
                          fontSize: 9,
                          textAlign: "center",
                          width: pairWidth,
                        },
                      });
                      if (stageBar) centeredBars.push({ ...stageBar });
                    }

                    const rawMax = Math.max(
                      ...centeredBars.map((b: any) => b.value ?? 0),
                      1,
                    );
                    const noOfSections = 4;
                    const stepValue = Math.ceil(rawMax / noOfSections) + 1;

                    const totalChartWidth = needsScroll
                      ? memberCount * (pairWidth + groupGap) +
                        initialSpace +
                        endSpace
                      : availableChartWidth;

                    const chartEl = (
                      <GroupedBarChart
                        data={centeredBars}
                        barWidth={barWidth}
                        width={
                          needsScroll ? totalChartWidth : availableChartWidth
                        }
                        noOfSections={noOfSections}
                        stepValue={stepValue}
                        maxValue={stepValue * noOfSections}
                        yAxisLabelWidth={yAxisWidth}
                        yAxisTextStyle={{
                          color: chartLabelColor,
                          fontSize: 10,
                        }}
                        height={chartHeight}
                        barBorderTopLeftRadius={3}
                        barBorderTopRightRadius={3}
                        rulesColor={chartRuleColor}
                        dashWidth={4}
                        dashGap={4}
                        spacing={groupGap}
                        initialSpacing={initialSpace}
                        endSpacing={endSpace}
                        onClick={(item) => {
                          const userId = item.userId as string | undefined;
                          if (userId)
                            setSelectedMember((prev) => ({
                              id: userId,
                              tick: (prev?.tick ?? 0) + 1,
                            }));
                        }}
                      />
                    );

                    return (
                      <Box direction="column" border rounded="md" paddingX="md" overflow="hidden">
                        {needsScroll ? (
                          <ScrollArea>
                            {chartEl}
                          </ScrollArea>
                        ) : (
                          chartEl
                        )}
                      </Box>
                    );
                  })()}
            </Box>

            <Separator orientation="vertical" />

            {/* Right: Table — 45% */}
            <Box direction="column" fill="flex" overflow="hidden" paddingLeft="md">
              {drilldownLoading ? (
                <Box fill="flex" align="center" justify="center">
                  <Spinner className="size-8" />
                </Box>
              ) : drilldown?.kind === "dailyActivity" ? (
                <DailyActivityTable
                  summaries={ageReasonGroupSummaries}
                  queryInput={{
                    startDate,
                    endDate,
                    teamIds,
                    memberIds,
                    subjectStartDate: drilldown.date,
                    subjectEndDate: drilldown.endDate,
                    timezone,
                    sortField,
                    sortDirection,
                  }}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  scrollToMemberId={selectedMember?.id ?? null}
                  scrollTick={selectedMember?.tick ?? 0}
                  pinProjectColumn={false}
                />
              ) : null}
            </Box>
          </Box>
        ) : (
          <Box overflow="hidden">
            <DataTableInDialog height={Math.max(200, tableHeight)}>
              {drilldownLoading ? (
                <Box fill="flex" align="center" justify="center">
                  <Spinner className="size-8" />
                </Box>
              ) : (isStageBreakdown || isWorkflowIssues) && drilldown ? (
                <StageBreakdownTable
                  summaries={issueGroupSummaries}
                  queryInput={{
                    startDate,
                    endDate,
                    teamIds,
                    memberIds,
                    stageName:
                      drilldown.kind === "stageBreakdown"
                        ? drilldown.stage
                        : undefined,
                    workflowName:
                      drilldown.kind === "workflowIssues"
                        ? drilldown.workflow
                        : undefined,
                    sortField,
                    sortDirection,
                  }}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  onClose={onClose}
                />
              ) : isAgeReasons ? (
                <AgeReasonDrilldown
                  summaries={ageReasonGroupSummaries}
                  queryInput={{
                    startDate,
                    endDate,
                    teamIds,
                    memberIds,
                    sortField,
                    sortDirection,
                  }}
                  sorting={sorting}
                  onSortingChange={setSorting}
                />
              ) : drilldown?.kind === "goldenHour" ? (
                <GoldenHourDrilldown
                  summaries={ageReasonGroupSummaries}
                  queryInput={{
                    startDate,
                    endDate,
                    teamIds,
                    memberIds,
                    hour: drilldown.hour,
                    timezone,
                    sortField,
                    sortDirection,
                  }}
                  sorting={sorting}
                  onSortingChange={setSorting}
                />
              ) : isTargetMetric ? (
                <TargetMetricDrilldown
                  summaries={activityGroupSummaries}
                  queryInput={{
                    startDate,
                    endDate,
                    teamIds,
                    memberIds,
                    sortField,
                    sortDirection,
                    timezone,
                  }}
                  sorting={sorting}
                  onSortingChange={setSorting}
                />
              ) : (
                <Box direction="column" paddingX="md">
                  <Box direction="row" justify="end" paddingBottom="xs">
                    <DataTableColumnSettings
                      columns={columns}
                      pinnedColumnIds={[fillColumnId]}
                      {...fallbackTableView.settingsProps}
                    />
                  </Box>
                  <DataTable
                    columns={columns}
                    groups={groups}
                    getRowId={(r) => r.id}
                    density="compact"
                    fillColumnId={fillColumnId}
                    pinnedColumnIds={[fillColumnId]}
                    stickyHeader
                    sorting={sorting}
                    onSortingChange={setSorting}
                    {...fallbackTableView.tableProps}
                  />
                </Box>
              )}
            </DataTableInDialog>
          </Box>
        )}

      {!usesSpecializedView && rows.length > 0 && (
        <Box paddingX="lg" paddingBottom="md" align="end">
          <Text size="xs" variant="muted">
            {rows.length} {rows.length === 1 ? "record" : "records"}
          </Text>
        </Box>
      )}
    </>
  );

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      {/* Near-fullscreen surface: 4rem of backdrop a side, `p-0` so the table
        * meets the edges, and the explicit size collapses the popup viewport's
        * fr rows. The surface is fixed — there is no resize affordance. */}
      <DialogPopup
        ref={popupRef}
        size="full" className="p-0"
      >
        <Box ref={headerRef} paddingX="md" paddingY="sm">
          <DialogHeader>
            <DialogTitle>{displayTitle}</DialogTitle>
            <DialogDescription>{subtitle}</DialogDescription>
          </DialogHeader>
        </Box>
        {body}
      </DialogPopup>
    </Dialog>
  );
}

// ── Data logic ───────────────────────────────────────────────────────────────

function mapActivityRow(a: DashboardDrilldownActivity): DrilldownRow {
  return {
    id: a.id,
    _issueId: a.issueId,
    _member: a.createdByName ?? "Unknown",
    _memberUserId: a.createdByUserId ?? undefined,
    project: a.issueName ?? `#${a.issueId}`,
    date: formatDateShort(a.creationDate),
    member: a.createdByName ?? "—",
    from: a.oldStageName ?? "—",
    to: a.newStageName ?? "—",
    reason:
      (a.reasonName && a.reasonName.toLowerCase() !== "updated"
        ? a.reasonName
        : null) ??
      (a.action && a.action.toLowerCase() !== "updated" ? a.action : null) ??
      "—",
    time: new Date(a.creationDate).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    }),
    address: a.issueAddress ?? "—",
    issueName: a.issueName ?? `#${a.issueId}`,
    type: a.subject === "age" ? "Age Update" : "Stage Change",
    detail:
      a.subject === "stage"
        ? `${a.oldStageName ?? "—"} → ${a.newStageName ?? "—"}`
        : (a.reasonName ?? "—"),
    action: a.action ?? "—",
  };
}

function mapIssueRow(issue: DashboardDrilldownIssue): DrilldownRow {
  return {
    id: issue.id,
    _issueId: issue.issueId,
    _workflowId: issue.workflowId ?? undefined,
    _member: issue.assigneeName ?? "Unknown",
    _assigneeUserId: issue.assigneeUserId ?? undefined,
    name: issue.name ?? `#${issue.issueId}`,
    address: issue.address ?? "—",
    assignee: issue.assigneeName ?? "—",
    stage: issue.stageName ?? "—",
    date: formatDateShort(issue.creationDate),
  };
}

function useDrilldownDataMemo({
  drilldown,
  activityData,
  issueData,
  labels,
  issueViewCtx,
  ageReasonGroupSummaries,
}: {
  drilldown: DrilldownType | null;
  activityData: DashboardDrilldownActivity[];
  issueData: DashboardDrilldownIssue[];
  labels: DashboardLabels | null | undefined;
  issueViewCtx: { openIssueById: (issueId: string) => void } | null | undefined;
  ageReasonGroupSummaries: AgeReasonGroupSummary[];
}): {
  title: string;
  description: string;
  columns: DataTableColumnDef<DrilldownRow>[];
  rows: DrilldownRow[];
} {
  return useMemo(() => {
    if (!drilldown)
      return { title: "", description: "", columns: [], rows: [] };

    const openIssue = (row: DrilldownRow) => {
      if (row._issueId) issueViewCtx?.openIssueById(row._issueId);
    };

    const issueLabel = (row: DrilldownRow, name: string | undefined) => (
      <DrilldownIssueLabel
        issueId={row._issueId ?? row.id}
        name={name ?? "—"}
        workflowId={row._workflowId}
      />
    );

    const memberCell = (userId: string | undefined, name: string | undefined) => (
      <DataTableCellContent>
        <DrilldownMemberTag
          userId={userId}
          name={name ?? "—"}
          size="sm"
          weight="regular"
        />
      </DataTableCellContent>
    );

    const projectCol = () =>
      h.hoverAction({
        accessorKey: "project",
        header: "Project",
        meta: { width: 180 },
        content: (row) => issueLabel(row, row.project),
        onClickAction: openIssue,
        actionLabel: (row) => `Open ${row.project}`,
      });

    const issueCol = () =>
      h.hoverAction({
        accessorKey: "name",
        header: "Project",
        content: (row) => issueLabel(row, row.name),
        onClickAction: openIssue,
        actionLabel: (row) => `Open ${row.name}`,
      });

    const issueNameCol = () =>
      h.hoverAction({
        accessorKey: "issueName",
        header: "Project",
        content: (row) => issueLabel(row, row.issueName),
        onClickAction: openIssue,
        actionLabel: (row) => `Open ${row.issueName}`,
      });

    switch (drilldown.kind) {
      case "stageChanges":
        return {
          title: "Stage Changes",
          description: `${activityData.length} records`,
          columns: [
            projectCol(),
            h.accessor({
              accessorKey: "date",
              header: "Date",
              enableSorting: true,
            }),
            h.accessor({
              accessorKey: "member",
              header: "Member",
              enableSorting: true,
              cell: ({ row }) =>
                memberCell(row.original._memberUserId, row.original.member),
            }),
            h.accessor({
              accessorKey: "from",
              header: "From Stage",
              enableSorting: true,
            }),
            h.accessor({
              accessorKey: "to",
              header: "To Stage",
              enableSorting: true,
            }),
          ],
          rows: activityData.map(mapActivityRow),
        };

      case "targetMetric":
        // Target metric now uses TargetMetricDrilldown — columns/rows unused
        return {
          title: labels?.targetMetricLabel ?? "Target Metric",
          description: "",
          columns: [],
          rows: [],
        };

      case "ageReasons": {
        const totalAgeUpdates = ageReasonGroupSummaries.reduce(
          (sum, s) => sum + s.totalUpdates,
          0,
        );
        return {
          title: "Age Reason Breakdown",
          description: `${totalAgeUpdates} age updates`,
          columns: [
            h.hoverAction({
              accessorKey: "project",
              header: "Project",
              content: (row) => (
                <Text size="sm" truncate={1}>
                  {row.project ?? "—"}
                </Text>
              ),
              onClickAction: openIssue,
              actionLabel: (row) => `Open ${row.project}`,
            }),
            h.accessor({
              accessorKey: "reason",
              header: "Reason",
              enableSorting: true,
              meta: { minWidth: 200 },
            }),
            h.accessor({
              accessorKey: "date",
              header: "Date",
              enableSorting: true,
            }),
            h.accessor({
              accessorKey: "member",
              header: "Member",
              enableSorting: true,
              meta: { minWidth: 200 },
            }),
          ],
          rows: activityData.map(mapActivityRow),
        };
      }

      case "goldenHour":
        return {
          title: `Activity at ${drilldown.label}`,
          description: `${activityData.length} age updates at this hour`,
          columns: [
            h.hoverAction({
              accessorKey: "project",
              header: "Project",
              content: (row) => (
                <Text size="sm" truncate={1}>
                  {row.project ?? "\u2014"}
                </Text>
              ),
              onClickAction: openIssue,
              actionLabel: (row) => `Open ${row.project}`,
            }),
            h.accessor({
              accessorKey: "reason",
              header: "Reason",
              enableSorting: true,
              meta: { minWidth: 200 },
            }),
            h.accessor({
              accessorKey: "time",
              header: "Time",
              enableSorting: true,
            }),
            h.accessor({
              accessorKey: "address",
              header: "Address",
              enableSorting: true,
              meta: { minWidth: 350 },
            }),
          ],
          rows: activityData.map(mapActivityRow),
        };

      case "dailyActivity":
        return {
          title: `Activity — ${drilldown.label}`,
          description: `${activityData.length} activities ${drilldown.endDate ? "this week" : "on this day"}`,
          columns: [
            h.hoverAction({
              accessorKey: "issueName",
              header: "Project",
              content: (row) => (
                <Text size="sm" truncate={1}>
                  {row.issueName ?? "—"}
                </Text>
              ),
              onClickAction: openIssue,
              actionLabel: (row) => `Open ${row.issueName}`,
            }),
            h.accessor({ accessorKey: "type", header: "Type" }),
            h.accessor({
              accessorKey: "member",
              header: "Member",
              meta: { minWidth: 200 },
            }),
            h.accessor({
              accessorKey: "detail",
              header: "Detail",
              meta: { minWidth: 350 },
            }),
          ],
          rows: activityData.map(mapActivityRow),
        };

      case "stageBreakdown":
        return {
          title: `Stage — ${drilldown.stage}`,
          description: `${issueData.length} projects in this stage`,
          columns: [
            issueCol(),
            h.accessor({ accessorKey: "address", header: "Address" }),
            h.accessor({
              accessorKey: "assignee",
              header: "Assignee",
              cell: ({ row }) =>
                memberCell(row.original._assigneeUserId, row.original.assignee),
            }),
            h.accessor({ accessorKey: "date", header: "Created" }),
          ],
          rows: issueData.map(mapIssueRow),
        };

      case "repActivity":
        return {
          title: `Age Updates — ${drilldown.name}`,
          description: `${activityData.length} age updates by this member`,
          columns: [
            issueNameCol(),
            h.accessor({
              accessorKey: "date",
              header: "Date",
              enableSorting: true,
            }),
            h.accessor({
              accessorKey: "action",
              header: "Action",
              enableSorting: true,
            }),
            h.accessor({
              accessorKey: "reason",
              header: "Reason",
              enableSorting: true,
            }),
          ],
          rows: activityData.map(mapActivityRow),
        };

      case "workflowIssues":
        return {
          title: `Workflow — ${drilldown.workflow}`,
          description: `${issueData.length} projects in this workflow`,
          columns: [
            issueCol(),
            h.accessor({
              accessorKey: "stage",
              header: "Stage",
              meta: { minWidth: 200 },
            }),
            h.accessor({
              accessorKey: "assignee",
              header: "Assignee",
              meta: { minWidth: 200 },
              cell: ({ row }) =>
                memberCell(row.original._assigneeUserId, row.original.assignee),
            }),
            h.accessor({ accessorKey: "date", header: "Created" }),
          ],
          rows: issueData.map(mapIssueRow),
        };
    }
  }, [
    drilldown,
    activityData,
    issueData,
    labels,
    issueViewCtx,
    ageReasonGroupSummaries,
  ]);
}
